import { useState, useEffect} from "react";
import { FiEdit2, FiX } from "react-icons/fi";
import { listProjects, getTeamMembers } from "../../Services/pm_service";
import { Dropdown } from "@iq/models";

function byNewestFirst(a, b) {
    const aTime = new Date(a?.created_at || a?.createdAt || 0).getTime();
    const bTime = new Date(b?.created_at || b?.createdAt || 0).getTime();
    return bTime - aTime;
}

function normalizeProjectList(projects) {
    return (Array.isArray(projects) ? projects : []).slice().sort(byNewestFirst);
}
function getInitialsByName(name){
	return name.trim().split(/\s+/).slice(0, 2).map((initial) => initial[0]?.toUpperCase()).join("");
}

const PROJECT_STORAGE_KEY = "pm_kanban_selected_project_id";
const PEOPLE_STORAGE_KEY = "pm_kanban_selected_people_ids";

function readSessionValue(key) {
    try {
        return sessionStorage.getItem(key);
    } catch (err) {
        console.error(`Failed reading "${key}" from sessionStorage`, err);
        return null;
    }
}

function writeSessionValue(key, value) {
    try {
        sessionStorage.setItem(key, value);
    } catch (err) {
        console.error(`Failed writing "${key}" to sessionStorage`, err);
    }
}


function Filter({selectedProject, onProjectChange, tasks, onFilteredTasksChange, projectsLoading, tasksLoading, onCreateProject, onEditProject}){
    const [projects, setProjects] = useState([]);
    const [members, setMembers] = useState([]);
    const [selectedPeople, setSelectedPeople] = useState([]);
    const [peopleRestored, setPeopleRestored] = useState(false);
    useEffect(() => {
        let cancelled = false;

        async function loadProjects(){
            try {
                const data = await listProjects();

                if(!cancelled){
                    setProjects(normalizeProjectList(data));
                }

            } catch(err){
                console.error("Failed loading projects", err);
            }
        }

        loadProjects();

        return () => {
            cancelled = true;
        };
    }, []);
    useEffect(() => {
        if(selectedProject || projects.length === 0){
            return;
        }

        const savedId = readSessionValue(PROJECT_STORAGE_KEY);
        const savedProject = savedId
            ? projects.find((project) => String(project.id) === savedId)
            : null;

        onProjectChange(savedProject || projects[0]);
    }, [projects, selectedProject, onProjectChange]);
    useEffect(() => {
        if(selectedProject?.id != null){
            writeSessionValue(PROJECT_STORAGE_KEY, String(selectedProject.id));
        }
    }, [selectedProject]);

    useEffect(() => {
            
        let cancelled = false;
        
    
        async function loadMembers(){
            try {
                const members = await getTeamMembers();
                if(!cancelled){
                    members.sort()
                    setMembers([...members].sort((a, b) => a.name.localeCompare(b.name)));
                }
                    
            } catch (err) {
                console.error("There was an error loading the team members: ", err);
                    
            }
        }
    
        loadMembers();
        return () => {
            cancelled = true;
        }
    }, [])

    useEffect(() => {
        if(members.length === 0){
            return;
        }

        try {
            const savedRaw = readSessionValue(PEOPLE_STORAGE_KEY);
            if(savedRaw){
                const savedIds = JSON.parse(savedRaw);
                if(Array.isArray(savedIds) && savedIds.length > 0){
                    const restored = members.filter((member) => savedIds.includes(member.id));
                    if(restored.length > 0){
                        setSelectedPeople(restored);
                    }
                }
            }
        } catch (err) {
            console.error("Failed to restore selected people filter", err);
        } finally {
            setPeopleRestored(true);
        }
    }, [members]);

    useEffect(() => {
        if(!peopleRestored){
            return;
        }
        writeSessionValue(PEOPLE_STORAGE_KEY, JSON.stringify(selectedPeople.map((person) => person.id)));
    }, [selectedPeople, peopleRestored]);


    useEffect(() =>{
        if(selectedPeople.length === 0){
            onFilteredTasksChange(tasks);
            return;
        }

        const ids = selectedPeople.map(person => person.id);

        const filtered = tasks.filter(task => {
            return ids.includes(task.assigned_user_id)
        })
        onFilteredTasksChange(filtered);
    }, [tasks, selectedPeople, onFilteredTasksChange])


	return(
        <div className="pm-kanban-header-controls">
					<div className="pm-project-dropdown-wrap">
						<Dropdown
							value={selectedProject?.title || "No Project Found"}
							allowCustom={false}
							colorScheme="light"
							className="pm-project-dropdown"
							inputClassName="pm-project-dropdown-input"
							menuClassName="pm-project-dropdown-menu"
							ariaLabel="Select project"
							disabled={projectsLoading || tasksLoading}
							renderInPortal
						>
							{({ closeMenu }) => (
								<>
									{projects.length > 0 ? (
										projects.map((project) => (
											<button
												type="button"
												key={project.id}
												className="dropdown-menu-item"
												onClick={() => {
													onProjectChange(project);
													closeMenu();
												}}
											>
												{project.title || `Project #${project.id}`}
											</button>
										))
									) : (
										<div className="dropdown-menu-empty">No projects found.</div>
									)}
									<button
										type="button"
										className="dropdown-menu-item dropdown-menu-item-create"
										onClick={() => {
											closeMenu();
											onCreateProject();
										}}
									>
										+ Create new project
									</button>
								</>
							)}
						</Dropdown>
					</div>
					<button
						type="button"
						className="pm-project-edit-button"
						onClick={onEditProject}
						disabled={!selectedProject || projectsLoading || tasksLoading}
						aria-label="Edit project"
						title="Edit project"
					>
						<FiEdit2 size={16} />
					</button>

					
					<Dropdown
						value="Filter by person"
							colorScheme="dark"
							className="pm-person-dropdown"
							inputClassName="pm-person-dropdown-input"
							menuClassName="pm-person-dropdown-menu"
							ariaLabel="Filter by person"
							renderInPortal
						>
								{({ closeMenu }) => (
								<>
									{members.length > 0 ? (
										members.map((member) => (
											<button
												type="button"
												key={member.id}
												className="dropdown-menu-item"
												onClick={() => {
													setSelectedPeople((current) => {
														if(current.some(person => person.id === member.id)){
															return current;
														}
														return [...current, member];
													});
													closeMenu();
												}}
											>
												{member.name || `Member #${member.id}`}
											</button>
										))
									) : (
										<div className="dropdown-menu-empty">No members listed.</div>
									)}
								
								</>
							)}
						</Dropdown>

					{selectedPeople.map((person) => (
						<div  key={person.id} className="pm-person-badge" title={person.name}>
							<span>{getInitialsByName(person.name)}</span>

							<FiX size={16}onClick={() => {
								setSelectedPeople((current => current.filter(selected => selected.id !== person.id)
								))
							}}/>
						</div>
					
					))}
				
					
				</div>
			

    );
        

}
export default Filter;